import { useRef, useState } from "react";

import { faFilter } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Drawer, List, Stack } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import invariant from "ts-invariant";

import { EducationIcon } from "assets";
import { useProfileContext } from "contexts/ProfileContext";
import { type AilmentType } from "routes/calendar/helpers/getAilments";
import { FilterSection, type Option, ResourceItem } from "routes/wiki/components";
import { type ResourceCategory, ailments, getResources } from "routes/wiki/data/resources";
import { BaseButton } from "shared/atoms/BaseButton";
import { Notification } from "shared/atoms/Notification";
import { getPatientSupportPhone, getSupportEmailLink, getSupportLink } from "utils/market";

const categoryOptionValues: ResourceCategory[] = ["evaluation", "guidelines", "payroll", "video"];

export const ResourceView: React.FC = () => {
  const { profile } = useProfileContext();

  const { t } = useTranslation();
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>();
  const [selectedCategory, setSelectedCategory] = useState<Option | undefined>();
  const [selectedAilment, setSelectedAilment] = useState<Option | undefined>();
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const categoryOptions = categoryOptionValues.map(option => ({
    value: option,
    title: t(`wiki.filter_options.${option}`),
  }));

  const ailmentOptions = ailments.map(option => ({
    value: option,
    title: t(`patients.pain_location.${option}`)[0],
  }));

  const scrollContainerIntoView = () => {
    containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  invariant(profile);

  const resources = getResources(profile?.market).filter(({ categories, ailments: resourceAilments }) => {
    if (selectedCategory && !categories?.includes(selectedCategory.value as ResourceCategory)) {
      return false;
    }
    if (selectedAilment && !resourceAilments?.includes(selectedAilment.value as AilmentType)) {
      return false;
    }
    return true;
  });

  const renderFilters = () => (
    <FilterArea>
      <FilterTitle>
        <FontAwesomeIcon icon={faFilter} color={theme.colors.primary.base} />
        {t("patients.filters")}
      </FilterTitle>

      <FilterSection
        title={t("wiki.filters.categories")}
        options={categoryOptions}
        selectedOption={selectedCategory}
        onChange={option => {
          setSelectedCategory(option);
          scrollContainerIntoView();
          setFilterDrawerOpen(false);
        }}
      />

      <FilterSection
        title={t("wiki.filters.ailments")}
        options={ailmentOptions}
        selectedOption={selectedAilment}
        onChange={option => {
          setSelectedAilment(option);
          scrollContainerIntoView();
          setFilterDrawerOpen(false);
        }}
      />
    </FilterArea>
  );

  return (
    <PageContainer>
      <Container ref={containerRef}>
        <Header>
          <EducationIcon />
          {t("wiki.page_header")}
        </Header>
        <Text>{t("wiki.page_text")}</Text>

        <Content>
          <DesktopWrapper>{renderFilters()}</DesktopWrapper>
          <MobileWrapper>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
              <BaseButton onClick={() => setFilterDrawerOpen(true)} variant="secondary" style={{ flexGrow: 0 }} small>
                <FontAwesomeIcon icon={faFilter} />
                {t("patients.filters")}
              </BaseButton>

              {(selectedCategory || selectedAilment) && (
                <BaseButton
                  onClick={() => {
                    setSelectedCategory(undefined);
                    setSelectedAilment(undefined);
                    scrollContainerIntoView();
                  }}
                  text={t("wiki.clear_filters")}
                  icon="xmark"
                  variant="text"
                  style={{ flexGrow: 0 }}
                  small
                />
              )}
            </Box>

            <Drawer open={filterDrawerOpen} onClose={() => setFilterDrawerOpen(false)}>
              {renderFilters()}
            </Drawer>
          </MobileWrapper>

          <Box sx={{ width: "100%" }}>
            <List sx={{ width: "100%", mb: 2, pt: 0 }}>
              {resources.map(resource => (
                <ResourceItem key={resource.title} resource={resource} />
              ))}
            </List>

            {selectedCategory?.value === "payroll" && (
              <Notification>
                <span>
                  {/* eslint-disable-next-line jsx-a11y/control-has-associated-label, jsx-a11y/anchor-has-content */}
                  <Trans i18nKey="wiki.payroll_info" components={[<a href="mailto:invoices@jointacademy.com" />]} />
                </span>
              </Notification>
            )}

            <Stack alignItems="center" mt={2}>
              <SupportText>{t("wiki.support_text")}</SupportText>
              <BaseButton
                text="Support"
                icon="circle-info"
                onClick={() => window.open(getSupportLink(profile?.market), "_blank")}
                style={{ flexGrow: 0, marginBottom: "12px" }}
              />
              <SupportText>
                <SupportTextTitle>{t("wiki.patient_support")}</SupportTextTitle>
                <span>{getPatientSupportPhone(profile?.market)}</span>
                <span>{getSupportEmailLink()}</span>
              </SupportText>
            </Stack>
          </Box>
        </Content>
      </Container>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  height: 100vh;
  overflow-y: auto;
  padding: 0 16px;
`;

const Container = styled(Box)`
  max-width: 900px;
  margin: 68px auto 100px auto;
  padding: 32px 24px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.redesign.db20};
  border-radius: 8px;
  position: relative;
  scroll-margin-top: 22px;

  ${props => props.theme.belowBreakpoint} {
    margin: 0;
  }
`;

const DesktopWrapper = styled.div`
  display: block;
  ${props => props.theme.belowBreakpoint} {
    display: none;
  }
`;

const MobileWrapper = styled.div`
  display: none;
  ${props => props.theme.belowBreakpoint} {
    display: block;
  }
`;

const Content = styled.div`
  display: flex;
  gap: 16px;
  flex-direction: row;

  ${props => props.theme.belowBreakpoint} {
    flex-direction: column;
  }
`;

const Header = styled.header`
  font-size: 24px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.primary.base};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const Text = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.primary.base};
  margin-bottom: 32px;
  text-align: center;
`;

const FilterArea = styled(Box)`
  width: 240px;
  position: relative;
  padding-right: 16px;
  border-right: 1px solid ${({ theme }) => theme.colors.redesign.db20};
  ${props => props.theme.belowBreakpoint} {
    padding: 24px 16px;
    border-right: none;
  }
`;

const FilterTitle = styled.div`
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 22px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SupportTextTitle = styled.span`
  font-weight: 500;
  margin-bottom: 4px;
  display: block;
`;

const SupportText = styled.p`
  display: flex;
  flex-direction: column;
  font-size: 14px;
  text-align: center;
  color: ${({ theme }) => theme.colors.primary.light};
`;
